import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import {
  Grid, IconButton, Tooltip, Fade,
} from '@material-ui/core';

import clsx from 'clsx';
import UploadPanelStyles from './styles';
import { StreamMeta, UploadRecordContext } from '../../record/UploadRecordContext';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  SycnIcon,
  DeleteIcon,
} from '../../../icon/solid';
import Progress from './Progress';
import usePrevious from '../../../../utility/usePrevious';
import { downloadStream } from '../../utils';

type Props = {
  stream: StreamMeta;
  openDeleteModal: (stream:StreamMeta) => (e: React.MouseEvent)=>void;
};

const RecordingItem = ({ stream, openDeleteModal }: Props) => {
  const classes = UploadPanelStyles();

  const { name, percent, status, timestamp } = stream;
  const {
    restart, setOpenPreviewModal, setCurrentStreamId,
  } = useContext(UploadRecordContext);

  const [showRemoveIconAfterUploading, setShowRemoveIconAfterUploading] = useState<boolean>(false);
  const prevStatus = usePrevious(status);
  const handleRestart = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    restart(stream);
  }, [stream, restart]);
  useEffect(() => {
    if (prevStatus === 'uploading' && status === 'done') {
      const timer = setTimeout(() => {
        setShowRemoveIconAfterUploading(true);
        clearTimeout(timer);
      }, 3000);
    }
  }, [status, prevStatus]);

  useEffect(() => {
    if (status === 'done') {
      setShowRemoveIconAfterUploading(true);
    }
    // eslint-disable-next-line
  }, []);

  const downloadVideo = useCallback((e:React.MouseEvent) => {
    if (e.altKey) {
      downloadStream(stream, name, 'video/webm');
      e.stopPropagation();
    }
  }, [stream, name]);

  const openPreview = useCallback(() => {
    setCurrentStreamId(stream.id);
    setOpenPreviewModal(true);
  }, [setOpenPreviewModal, setCurrentStreamId, stream]);

  return (
    <Grid
      onClick={openPreview}
      item
      container
      alignItems="center"
      className={classes.preview}
    >
      <Grid item className={classes.titleContainer}>
        <Grid item className={classes.title}>
          {name}
        </Grid>
      </Grid>
      <Grid item className={classes.actionContainer}>
        {
          status !== 'aborted' && (
            ['exception_upload', 'error_upload', 'error'].includes(status)
              ? (
                <Tooltip title="File upload unsuccessful">
                  <ExclamationCircleIcon className={classes.errorIcon} />
                </Tooltip>
              )

              : (status === 'done')
                ? (
                  <Grid container alignItems="center" className={classes.uploadingContainer}>
                    <span onClick={downloadVideo}>{timestamp}</span>
                    <div className={classes.iconContainer}>
                      <Fade in={!showRemoveIconAfterUploading} timeout={500} unmountOnExit>
                        <CheckCircleIcon className={clsx(classes.icon, classes.successIcon)} />
                      </Fade>
                      <Fade in={showRemoveIconAfterUploading} timeout={500} unmountOnExit>
                        <DeleteIcon className={clsx(classes.icon, 'removeAbsoluteIcon')} onClick={openDeleteModal(stream)} />
                      </Fade>
                    </div>
                  </Grid>
                )
                : (
                  <Grid container alignItems="center" className={classes.uploadingContainer} onClick={downloadVideo}>
                    <span className="uploading" >Uploading</span>
                    <Progress value={percent} isStatic={(!['ready'].includes(status))} />
                  </Grid>
                )
          )
        }
      </Grid>
      <Grid item>
        {
          (!['done'].includes(status))
          && (
            <IconButton title="Remove" onClick={openDeleteModal(stream)} className={clsx(classes.action, showRemoveIconAfterUploading ? classes.removeMargin : '')}>
              <DeleteIcon className={classes.icon} />
            </IconButton>
          )
        }
        {
          ['exception_upload', 'error_upload', 'error'].includes(status)
          && (
            <IconButton onClick={handleRestart} title="Restart upload" className={classes.action}>
              <SycnIcon className={classes.icon} />
            </IconButton>
          )
        }
      </Grid>
    </Grid>
  );
};

export default RecordingItem;
